import { BaseWidgetContainer } from 'components/page/BaseLandingPage';
import { styled } from 'styled-components';
import { tokens } from 'styles/tokens';

/**
 * Styled components for the Location Hardware Status widget in the Location page
 * Displays a grid of cards showing hardware devices with their counts
 */

/**
 * Main container for the Location Hardware Status widget
 * Uses consistent padding, background, and shadow from theme
 */
export const Container = styled(BaseWidgetContainer)`
    gap: 1.2rem;
    overflow: visible;
    height: auto !important;
`;

/**
 * Widget title component
 * Uses standardized heading style with specific line height for alignment
 */
export const Title = styled.h2`
    font-size: 2.4rem;
    font-weight: 500;
    line-height: 1.25; /* 30px / 24px = 1.25 */
    color: ${tokens.text.primary};
    margin: 0;
`;

/**
 * Grid layout for hardware cards
 * Responsive with flex-wrap to handle different screen sizes
 */
export const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(16rem, 1fr));
    gap: 1.2rem;
    flex-grow: 1;
`;

/**
 * Individual hardware status card
 * Responsive sizing for different screen layouts
 */
export const Card = styled.div`
    background-color: ${tokens.background.paperElevation4};
    border: 1px solid ${tokens.divider.default};
    border-radius: 0.8rem;
    padding: 1.6rem;
    position: relative;
    cursor: pointer;
    transition: box-shadow 0.2s ease-in-out;
    box-sizing: border-box;
    height: 8.9rem; /* Fixed height for consistent card sizing */

    &:hover {
        box-shadow: 0 0.2rem 0.8rem rgba(0, 0, 0, 0.12);
    }
`;

/**
 * Link wrapper for card
 * Extends clickable area to entire card using ::before pseudo-element
 * Implements accessible focus states
 */
export const CardLink = styled.a`
    text-decoration: none;
    color: inherit;
    display: block;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 1;
    }

    &:focus {
        outline: none;
    }

    &:focus-visible::before {
        outline: 0.2rem solid ${tokens.input.borderFocus};
        outline-offset: 0.2rem;
        border-radius: 0.8rem;
    }
`;

/**
 * Layout container for card content
 * Maintains consistent spacing between icon and text information
 */
export const CardContent = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
    min-height: 4.9rem;
    box-sizing: border-box;
`;

/**
 * Container for hardware name and count information
 * Uses flex column layout with space-between for vertical alignment
 */
export const HardwareInfo = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 4.9rem;
    justify-content: space-between;
    box-sizing: border-box;
    margin: 0;
`;

/**
 * Container for hardware type label
 * Fixed height to ensure consistent vertical alignment
 */
export const LabelContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 0.8rem;
    height: 2.4rem;
    margin: 0;
`;

/**
 * Text component for hardware type name
 * Uses standard body text styling from design system
 */
export const HardwareType = styled.span`
    color: ${tokens.text.primary};
    font-size: 1.4rem;
    font-style: normal;
    font-weight: 300;
    line-height: 1.43; /* 20px / 14px = 1.43 */
`;

/**
 * Container for online and total counts
 * Uses baseline alignment to properly align different text sizes
 */
export const CountContainer = styled.div`
    display: flex;
    align-items: baseline;
    gap: 0.4rem;
    line-height: 1;
`;

/**
 * Displays count of hardware devices
 * Large, prominent text to emphasize count
 */
export const Count = styled.span`
    color: ${tokens.text.primary};
    font-size: 2.9rem;
    font-weight: 500;
    line-height: 1.1; /* 32px / 29px = 1.1 */
`;

/**
 * Displays total hardware count
 * Smaller, secondary text to provide context to count
 */
export const TotalCount = styled.span`
    color: ${tokens.text.secondary};
    font-size: 1.5rem;
    font-weight: 500;
    line-height: 1.6; /* 24px / 15px = 1.6 */
`;

/**
 * Container for hardware type icon
 * Fixed size with consistent background and border radius
 */
export const IconContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 4.8rem;
    height: 4.8rem;
    background: ${tokens.grey.light};
    border-radius: 0.8rem;
    flex-shrink: 0;
    align-self: flex-start;

    svg {
        color: ${tokens.secondary.dark};
        width: 3.2rem;
        height: 3.2rem;
    }
`;

/**
 * Action button for hardware card
 * Uses z-index to ensure clickability inside CardLink
 * Implements accessible hover and focus states
 */
export const ActionButton = styled.a`
    display: flex;
    align-items: center;
    padding: 0.2rem 1.2rem;
    height: 2.4rem;
    background: ${tokens.grey.default};
    border-radius: 0.5rem;
    border: none;
    cursor: pointer;
    text-decoration: none;
    transition: background-color 0.2s ease-in-out;
    position: relative;
    z-index: 2; /* Ensures clickability on top of CardLink */

    &:hover {
        background: ${tokens.grey.dark};
    }

    &:focus {
        outline: none;
        box-shadow: 0 0 0 0.2rem ${tokens.input.borderFocus};
    }

    &:focus:not(:focus-visible) {
        outline: none;
    }

    svg {
        width: 1.4rem;
        height: 1.4rem;
        color: ${tokens.text.primary};
    }
`;

export const NoHardwareMessage = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1.2rem;
    color: ${tokens.text.secondary};
    font-size: 1.4rem;
    grid-column: 1 / -1;
`;
